import { graphqlRequest } from "./graphql";

// ── Credit Balance ──

interface CreditBalanceResponse {
  total: { aggregate: { sum: { credit: number | null } } };
}

export async function getCreditBalance(userId: string, token: string) {
  const query = `
    query GetCreditBalance($user_id: uuid!) {
      total: whatsub_b2b_credit_aggregate(where: {user_id: {_eq: $user_id}}) {
        aggregate {
          sum { credit }
        }
      }
    }
  `;
  const data = await graphqlRequest<CreditBalanceResponse>(query, { user_id: userId }, token);
  return data.total.aggregate.sum.credit ?? 0;
}

// ── Credit Analytics ──

interface AggBucket {
  aggregate: { sum: { credit: number | null } };
}

interface CreditAnalyticsResponse {
  today_spent: AggBucket;
  yesterday_spent: AggBucket;
  today_added: AggBucket;
  yesterday_added: AggBucket;
}

export interface CreditAnalytics {
  todaySpent: number;
  yesterdaySpent: number;
  todayAdded: number;
  yesterdayAdded: number;
}

export async function getCreditAnalytics(
  userId: string,
  todayStart: string,
  todayEnd: string,
  yesterdayStart: string,
  token: string
): Promise<CreditAnalytics> {
  const query = `
    query GetCreditAnalytics(
      $_lte: timestamptz!,
      $_gt: timestamptz!,
      $_gty: timestamptz!,
      $user_id: uuid!
    ) {
      today_spent: whatsub_b2b_credit_aggregate(
        where: {
          type: {_in: ["debit", "transfer"]},
          created_at: {_lte: $_lte, _gt: $_gt},
          user_id: {_eq: $user_id}
        }
      ) { aggregate { sum { credit } } }
      yesterday_spent: whatsub_b2b_credit_aggregate(
        where: {
          type: {_in: ["debit", "transfer"]},
          created_at: {_lte: $_gt, _gt: $_gty},
          user_id: {_eq: $user_id}
        }
      ) { aggregate { sum { credit } } }
      today_added: whatsub_b2b_credit_aggregate(
        where: {
          type: {_eq: "credit"},
          created_at: {_lte: $_lte, _gt: $_gt},
          user_id: {_eq: $user_id}
        }
      ) { aggregate { sum { credit } } }
      yesterday_added: whatsub_b2b_credit_aggregate(
        where: {
          type: {_eq: "credit"},
          created_at: {_lte: $_gt, _gt: $_gty},
          user_id: {_eq: $user_id}
        }
      ) { aggregate { sum { credit } } }
    }
  `;
  const data = await graphqlRequest<CreditAnalyticsResponse>(
    query,
    { _lte: todayEnd, _gt: todayStart, _gty: yesterdayStart, user_id: userId },
    token
  );
  return {
    todaySpent: data.today_spent.aggregate.sum.credit ?? 0,
    yesterdaySpent: data.yesterday_spent.aggregate.sum.credit ?? 0,
    todayAdded: data.today_added.aggregate.sum.credit ?? 0,
    yesterdayAdded: data.yesterday_added.aggregate.sum.credit ?? 0,
  };
}

// ── Credit Transactions (paginated) ──

export interface CreditTransactionRow {
  id: string;
  credit: number;
  created_at: string;
  updated_at: string;
  type: string;
  purpose: string;
  metadata: Record<string, unknown> | null;
  payment_id: string | null;
  from: string | null;
}

interface GetCreditTransactionsResponse {
  whatsub_b2b_credit: CreditTransactionRow[];
  whatsub_b2b_credit_aggregate: { aggregate: { count: number } };
}

export interface CreditTransactionFilters {
  userId: string;
  types?: string[];
  createdFrom?: string;
  createdTo?: string;
  search?: string;
}

function buildCreditWhere(filters: CreditTransactionFilters): Record<string, unknown> {
  const where: Record<string, unknown> = {
    user_id: { _eq: filters.userId },
  };
  if (filters.types && filters.types.length > 0) {
    where["type"] = { _in: filters.types };
  }
  const createdAt: Record<string, string> = {};
  if (filters.createdFrom) createdAt["_gte"] = filters.createdFrom;
  if (filters.createdTo) createdAt["_lte"] = filters.createdTo;
  if (Object.keys(createdAt).length > 0) {
    where["created_at"] = createdAt;
  }
  if (filters.search) {
    where["purpose"] = { _ilike: `%${filters.search}%` };
  }
  return where;
}

export async function getCreditTransactions(
  filters: CreditTransactionFilters,
  limit: number,
  offset: number,
  token: string
) {
  const query = `
    query GetCreditTransactions(
      $limit: Int!,
      $offset: Int!,
      $where: whatsub_b2b_credit_bool_exp!
    ) {
      whatsub_b2b_credit(
        limit: $limit,
        offset: $offset,
        order_by: { created_at: desc },
        where: $where
      ) {
        id
        credit
        created_at
        updated_at
        type
        purpose
        metadata
        payment_id
        from
      }
      whatsub_b2b_credit_aggregate(where: $where) {
        aggregate { count }
      }
    }
  `;
  const where = buildCreditWhere(filters);
  const data = await graphqlRequest<GetCreditTransactionsResponse>(
    query,
    { limit, offset, where },
    token
  );
  return {
    rows: data.whatsub_b2b_credit,
    total: data.whatsub_b2b_credit_aggregate.aggregate.count,
  };
}

// ── All Credit Transactions (for export) ──

interface GetAllCreditTransactionsResponse {
  whatsub_b2b_credit: CreditTransactionRow[];
}

export async function getAllCreditTransactions(
  filters: CreditTransactionFilters,
  token: string
) {
  const query = `
    query GetAllCreditTransactions($where: whatsub_b2b_credit_bool_exp!) {
      whatsub_b2b_credit(
        where: $where
        order_by: { created_at: desc }
      ) {
        id
        credit
        created_at
        updated_at
        type
        purpose
        metadata
        payment_id
        from
      }
    }
  `;
  const where = buildCreditWhere(filters);
  const data = await graphqlRequest<GetAllCreditTransactionsResponse>(
    query,
    { where },
    token
  );
  return data.whatsub_b2b_credit;
}

// ── Credit Transaction Types ──

interface GetCreditTypesResponse {
  whatsub_b2b_credit: Array<{ type: string }>;
}

export async function getCreditTransactionTypes(userId: string, token: string) {
  const query = `
    query GetCreditTransactionTypes($user_id: uuid!) {
      whatsub_b2b_credit(
        where: { user_id: { _eq: $user_id } }
        distinct_on: type
      ) {
        type
      }
    }
  `;
  const data = await graphqlRequest<GetCreditTypesResponse>(
    query,
    { user_id: userId },
    token
  );
  return data.whatsub_b2b_credit.map((t) => t.type);
}
